import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle as MuiDialogTitle,
  Divider,
  makeStyles,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import { Close, Error as ErrorIcon } from '@material-ui/icons';
import { Button, Card } from '@redshelf/redshelf-ui';
import ColorButtons from '../ColorButtons';
import IconButton from 'components/IconButton';
import { FocusTrap } from 'components';
import { SCREEN_SIZE_CATEGORIES } from 'constants';
import { COLOR_MAP, COLOR_PALETTES } from 'constants/appStyling/options';
import {
  AnalyticsModifiersContext,
  RailValuesContext,
  DocumentValuesContext,
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { UserContentNote } from 'types';
import { getLocalSettingsProxy } from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    cardContent: {
      padding: theme.spacing(1.5, 2, 2, 2),
      width: '100%',
    },
    textField: {
      margin: theme.spacing(2, 0),
      width: '100%',

      '& input + fieldset': {
        borderWidth: 2,
      },

      '& .Mui-error.MuiOutlinedInput-notchedOutline': {
        borderColor: '#D32F2F',
      },
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    errorIcon: {
      marginRight: theme.spacing(0.75),
      //errorMain is other colour in RSUI
      color: '#D32F2F',
    },
    errorMessage: {
      fontSize: theme.spacing(1.5),
      lineHeight: 2.5,
      color: '#D32F2F',
    },
    formHelperText: {
      margin: theme.spacing(0.5, 1, 0, 1),
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      margin: 0,
    },
    button: {
      margin: 0,
    },
    titleContainer: {
      display: 'flex',
      flex: '1',
      alignSelf: 'center',
      paddingLeft: theme.spacing(2),
    },
    titleRoot: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      padding: theme.spacing(0.33333333, 2), // 2px 16px
      minHeight: theme.spacing(6.75), // 48px + 6px === Close icon size by default + the container offset
    },
    title: {
      marginRight: theme.spacing(6), // 48px === Close icon size by default
      fontSize: '1.175rem',
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.33333333), // 2px
      right: theme.spacing(0.33333333),
    },
    borderLeft: ({ ribbonColor }) => {
      if (ribbonColor) {
        return {
          borderLeft: `solid 4px ${ribbonColor}`,
        };
      }
    },
    dialogPaper: {
      width: theme.spacing(34), // StyleCard's width
    },
    dialogContent: {
      padding: theme.spacing(1.5, 2, 2, 2),
    },
  };
});

const StyledCard = withStyles((theme) => {
  return {
    root: {
      display: 'flex',
      flexFlow: 'row wrap',
      width: theme.spacing(34),
      '&:focus-visible': {
        outline: 'none',
      },
    },
  };
})(Card);

const StyledDivider = withStyles((theme) => {
  return {
    root: {
      display: 'flex',
      flex: '1 100%',
    },
  };
})(Divider);

const getErrorMessageText = ({ color, text }) => {
  let missing;
  if (!text.trim() && !color) {
    missing = 'note and color';
  } else {
    missing = !text.trim() ? 'note' : 'color';
  }
  return `Please add a ${missing} to save.`;
};

const ErrorMessage = ({ errorMessageText }) => {
  const classes = useStyles();

  return (
    <div className={classes.errorContainer}>
      <ErrorIcon className={classes.errorIcon} titleAccess="Error" />
      <Typography id="note-editor-error" className={classes.errorMessage}>
        {errorMessageText}
      </Typography>
    </div>
  );
};

const NoteEditorBody = ({
  ButtonContainer,
  color,
  errorMessageText,
  firstColorButtonRef,
  handleColorButtonChange,
  handleTextFieldChange,
  text,
  textFieldRef,
}) => {
  const classes = useStyles();
  const error = Boolean(errorMessageText);

  return (
    <>
      <ColorButtons
        firstButtonRef={firstColorButtonRef}
        handleColorSelection={handleColorButtonChange}
        selectedColor={color}
        {...(error && !color
          ? {
              ariaDescribedby: 'note-editor-error',
              hasError: true,
            }
          : {})}
      />
      <TextField
        inputRef={textFieldRef}
        className={classes.textField}
        error={error && !text.trim()}
        id="note-field"
        FormHelperTextProps={{
          component: 'div',
          classes: { contained: classes.formHelperText },
        }}
        helperText={
          errorMessageText ? (
            <ErrorMessage errorMessageText={errorMessageText} />
          ) : null
        }
        inputProps={{ className: 'noTranslate' }}
        multiline
        onChange={handleTextFieldChange}
        placeholder="Note"
        rows={4}
        value={text}
        variant="outlined"
        label="Add Note"
        {...(error && !text.trim()
          ? {
              InputProps: {
                inputProps: { 'aria-describedby': 'note-editor-error' },
              },
            }
          : {})}
      />
      <ButtonContainer />
    </>
  );
};

const NoteEditor = ({
  isLearningToolsMenu,
  note,
  onClose = () => {},
  onSave = () => {},
  shouldShowAsDialog, // Allow override for testing
  ...props
}) => {
  const { isWithinRail, positionedElements } = useContext(RailValuesContext);
  const { highlightToEdit } = useContext(UserContentItemValuesContext);
  const {
    clearAllUserContent,
    onDialogClose,
    setSelectedNote,
    setAriaLiveMessageForUserContent,
  } = useContext(UserContentItemModifiersContext);
  const { frame } = useContext(DocumentValuesContext);
  const analytics = useContext(AnalyticsModifiersContext);

  const [localSettings] = useState(getLocalSettingsProxy);
  const paletteId = localSettings.appStyling.colorPalette;
  const highlightColors = COLOR_PALETTES[paletteId];

  const { viewport } = useContext(ViewportValuesContext);
  shouldShowAsDialog =
    shouldShowAsDialog !== undefined // Allow override for testing
      ? shouldShowAsDialog
      : viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const noteEditorRef = useRef();

  const [text, setText] = useState(note.userText || '');
  const [color, setColor] = useState(note.color);
  const [ribbonColor, setRibbonColor] = useState(null);
  const [errorMessageText, setErrorMessageText] = useState(null);
  const [givenFocusOnce, setGivenFocusOnce] = useState(false);
  const [noteTop, setNoteTop] = useState(null);

  const textFieldRef = useRef(null);
  const firstColorButtonRef = useRef(null);

  useEffect(
    function updateRibbonColor() {
      if (color && highlightColors) {
        const newRibbonColor = highlightColors[COLOR_MAP[color]].background;
        setRibbonColor((prevState) =>
          prevState !== newRibbonColor ? newRibbonColor : prevState,
        );
      }
    },
    [color, highlightColors],
  );

  const classes = useStyles({ ribbonColor });

  const title = note.userText ? 'Edit a Note' : 'Create a Note';

  const handleTextFieldChange = useCallback((event) => {
    setText(event.target.value);
    if (event.target.value.trim()) {
      setErrorMessageText(null);
    }
  }, []);

  const handleColorButtonChange = useCallback(
    (color) => {
      setColor(color);
      setErrorMessageText(null);
      if (!isLearningToolsMenu) {
        const newNote = new UserContentNote({
          cfi: note.cfi,
          uuid: note.uuid,
          color,
          document: note.document,
          snippet: note.snippet,
          userText: note.userText,
          userId: note.userId,
          firstName: note.userFirstName,
          lastName: note.userLastName,
        });

        setSelectedNote(newNote);
        //save happens on 'save' button press.
      }
    },
    [isLearningToolsMenu, note, setSelectedNote],
  );

  const handleClose = useCallback(() => {
    onClose();
    onDialogClose('noteEditor');
    clearAllUserContent();
  }, [clearAllUserContent, onClose, onDialogClose]);

  const addErrorFocusOnSave = useCallback(() => {
    if (!color && firstColorButtonRef) {
      firstColorButtonRef.current.focusVisible();
    }
    if (color && !text.trim() && textFieldRef?.current) {
      textFieldRef.current.focus();
    }
  }, [color, text]);

  const updateErrorMessageText = useCallback(() => {
    const newErrorMessageText = getErrorMessageText({ color, text });
    setErrorMessageText((prevState) =>
      prevState !== newErrorMessageText ? newErrorMessageText : prevState,
    );
  }, [color, text]);

  const handleSaveOnClick = useCallback(async () => {
    addErrorFocusOnSave();

    if (!text.trim() || !color) {
      updateErrorMessageText();
      return;
    }

    analytics.noteCreated({});
    if (highlightToEdit && highlightToEdit.cfi === note.cfi) {
      highlightToEdit.delete();
    }
    const savedNote = await onSave({
      userText: text,
      color,
      userId: note.userId,
    });
    setTimeout(() => {
      setAriaLiveMessageForUserContent({
        action: savedNote.isSaved ? 'updated' : 'created',
        color: savedNote.color,
        snippet: savedNote.snippet,
        type: savedNote.constructor.typeName,
      });
    }, 2000);
    handleClose();
  }, [
    analytics,
    color,
    highlightToEdit,
    note,
    handleClose,
    onSave,
    text,
    addErrorFocusOnSave,
    updateErrorMessageText,
    setAriaLiveMessageForUserContent,
  ]);

  const DialogTitle = () => {
    return (
      <MuiDialogTitle className={classes.titleRoot} disableTypography>
        <Typography
          id="note-editor-title"
          className={classes.title}
          variant="h2"
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      </MuiDialogTitle>
    );
  };

  const ButtonContainer = () => {
    return (
      <div className={classes.buttonContainer}>
        <Button
          className={classes.button}
          onClick={handleClose}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button className={classes.button} onClick={handleSaveOnClick}>
          Save
        </Button>
      </div>
    );
  };

  const CardTitle = () => {
    return (
      <>
        <div className={classes.titleContainer}>
          <Typography
            id="note-editor-title"
            variant="h2"
            className={classes.title}
          >
            {title}
          </Typography>
        </div>
        <IconButton aria-label="Close" onClick={handleClose}>
          <Close />
        </IconButton>
        <StyledDivider />
      </>
    );
  };

  /**
   * Once, on initial render in the rail, focus the note editor.
   * If the top of the node editor changes, meaning the document has been readjusted,
   * and the note goes out of view, scroll the note into view again and focus it.
   * When the editor itself is focused, pressing tab should focus the close button.
   */
  useEffect(() => {
    const top = noteEditorRef?.current?.parentElement?.offsetTop;
    if (
      isWithinRail &&
      noteEditorRef?.current &&
      positionedElements.includes(noteEditorRef.current) &&
      (!givenFocusOnce || noteTop !== top)
    ) {
      setNoteTop(top);

      // if note is not visible in viewport, scroll into view.
      noteEditorRef.current.scrollIntoView({
        block: 'nearest',
      });

      noteEditorRef.current.focus();
      setGivenFocusOnce(true);
    }
  }, [givenFocusOnce, isWithinRail, positionedElements, frame, noteTop]);

  return shouldShowAsDialog || isLearningToolsMenu ? (
    <Dialog
      aria-labelledby="note-editor-title"
      classes={{ paper: clsx(classes.dialogPaper, classes.borderLeft) }}
      onClose={(_, reason) => {
        // default MUI behaviour is to close on Escape key Down, but we want Dialog to close on keyUp not keyDown
        // so prevent close on KeyDown
        if (reason !== 'escapeKeyDown') {
          handleClose();
        }
      }}
      open={true}
      onKeyUp={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          handleClose();
        }
      }}
      PaperProps={{
        'data-color': color,
        'aria-modal': true,
      }}
      disablePortal
      {...props}
    >
      <DialogTitle />
      <DialogContent className={classes.dialogContent} dividers>
        <NoteEditorBody
          ButtonContainer={ButtonContainer}
          color={color}
          errorMessageText={errorMessageText}
          firstColorButtonRef={firstColorButtonRef}
          handleColorButtonChange={handleColorButtonChange}
          handleTextFieldChange={handleTextFieldChange}
          text={text}
          textFieldRef={textFieldRef}
        />
      </DialogContent>
    </Dialog>
  ) : (
    <FocusTrap>
      <StyledCard
        aria-labelledby="note-editor-title"
        aria-modal={false}
        classes={{
          root: classes.borderLeft,
        }}
        ref={noteEditorRef}
        role="dialog"
        tabIndex="0"
        {...props}
      >
        <CardTitle />
        <Box className={classes.cardContent}>
          <NoteEditorBody
            ButtonContainer={ButtonContainer}
            color={color}
            errorMessageText={errorMessageText}
            firstColorButtonRef={firstColorButtonRef}
            handleColorButtonChange={handleColorButtonChange}
            handleTextFieldChange={handleTextFieldChange}
            text={text}
            textFieldRef={textFieldRef}
          />
        </Box>
      </StyledCard>
    </FocusTrap>
  );
};

NoteEditor.propTypes = {
  isLearningToolsMenu: PropTypes.bool,
  note: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  shouldShowAsDialog: PropTypes.bool,
};

export default NoteEditor;
